import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  KatanaPathDumpQueryParams,
  KatanaPathDumpRow,
  KatanaPathDumpView,
  KatanaPoint,
} from '@/types';
import {
  advancedToggleColumnKeys,
  defaultLimit,
  toTodayDate,
  uniqueNumbers,
} from './constants';
import { PointMode, PointSelection } from './types';

const useDebouncedValue = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
};

export const useKatanaExplorerFilters = () => {
  const [startDateInput, setStartDateInput] = useState(toTodayDate());
  const [endDateInput, setEndDateInput] = useState('');
  const [limitInput, setLimitInput] = useState(defaultLimit);

  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [offset, setOffset] = useState(0);
  const [clientError, setClientError] = useState<string | null>(null);

  const [katanaTspSearch, setKatanaTspSearch] = useState('');
  const [katanaTspPageNumber, setKatanaTspPageNumber] = useState(1);
  const [useAllKatanaTspIds, setUseAllKatanaTspIds] = useState(true);
  const [selectedKatanaTspIds, setSelectedKatanaTspIds] = useState<number[]>([]);

  const [receiptSelection, setReceiptSelection] = useState<PointSelection>({
    ids: [],
    names: {},
  });
  const [deliverySelection, setDeliverySelection] = useState<PointSelection>({
    ids: [],
    names: {},
  });

  const [activePointModal, setActivePointModal] = useState<PointMode | null>(null);
  const [pointModalSearch, setPointModalSearch] = useState('');
  const [pointModalPageNumber, setPointModalPageNumber] = useState(1);
  const [pointModalKatanaTspId, setPointModalKatanaTspId] = useState<
    number | undefined
  >(undefined);

  const [rowSearchInput, setRowSearchInput] = useState('');
  const rowSearchTerm = useDebouncedValue(rowSearchInput.trim().toLowerCase(), 120);
  const [viewMode, setViewMode] = useState<KatanaPathDumpView>('summary');

  const [appliedFilters, setAppliedFilters] = useState<KatanaPathDumpQueryParams>({
    start_date: toTodayDate(),
    view: 'summary',
    limit: defaultLimit,
    offset: 0,
  });

  const [hiddenColumns, setHiddenColumns] = useState<Set<keyof KatanaPathDumpRow>>(
    () => new Set(advancedToggleColumnKeys),
  );

  const selectedKatanaTspIdsSorted = useMemo(
    () => uniqueNumbers(selectedKatanaTspIds),
    [selectedKatanaTspIds],
  );

  const debouncedTspSearch = useDebouncedValue(katanaTspSearch.trim(), 250);
  const debouncedPointSearch = useDebouncedValue(pointModalSearch.trim(), 250);

  const toggleTspSelection = useCallback((id: number) => {
    setSelectedKatanaTspIds((prev) => {
      if (prev.includes(id)) return prev.filter((value) => value !== id);
      return uniqueNumbers([...prev, id]);
    });
  }, []);

  const togglePointSelection = useCallback((mode: PointMode, point: KatanaPoint) => {
    const setSelection = mode === 'receipt' ? setReceiptSelection : setDeliverySelection;

    setSelection((prev) => {
      const isSelected = prev.ids.includes(point.id);
      const nextIds = isSelected
        ? prev.ids.filter((value) => value !== point.id)
        : uniqueNumbers([...prev.ids, point.id]);
      const nextNames = { ...prev.names };

      if (isSelected) {
        delete nextNames[point.id];
      } else {
        nextNames[point.id] = point.name || 'Unnamed point';
      }

      return { ids: nextIds, names: nextNames };
    });
  }, []);

  const clearSelectedPoints = useCallback((mode: PointMode) => {
    if (mode === 'receipt') {
      setReceiptSelection({ ids: [], names: {} });
      return;
    }
    setDeliverySelection({ ids: [], names: {} });
  }, []);

  const openPointModal = useCallback((mode: PointMode) => {
    setActivePointModal(mode);
    setPointModalSearch('');
    setPointModalPageNumber(1);
    setPointModalKatanaTspId(undefined);
  }, []);

  const closePointModal = useCallback(() => {
    setActivePointModal(null);
  }, []);

  const updatePointModalSearch = useCallback((value: string) => {
    setPointModalSearch(value);
    setPointModalPageNumber(1);
  }, []);

  const updatePointModalPipeline = useCallback((value: number | undefined) => {
    setPointModalKatanaTspId(value);
    setPointModalPageNumber(1);
  }, []);

  const toggleAdvancedColumns = useCallback(() => {
    setHiddenColumns((prev) => {
      const allHidden = advancedToggleColumnKeys.every((key) => prev.has(key));
      const next = new Set(prev);
      advancedToggleColumnKeys.forEach((key) => {
        if (allHidden) next.delete(key);
        else next.add(key);
      });
      return next;
    });
  }, []);

  const toggleColumn = useCallback((key: keyof KatanaPathDumpRow) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const onApplyFilters = useCallback((evt: React.FormEvent) => {
    evt.preventDefault();
    setClientError(null);

    if (startDateInput && endDateInput && endDateInput < startDateInput) {
      setClientError('End date cannot be earlier than start date.');
      return;
    }

    const nextLimit =
      Number.isFinite(limitInput) && limitInput > 0 ? limitInput : defaultLimit;

    setOffset(0);
    setHasAppliedFilters(true);
    setAppliedFilters({
      start_date: startDateInput || undefined,
      end_date: endDateInput || undefined,
      tsp_ids:
        useAllKatanaTspIds || selectedKatanaTspIdsSorted.length === 0
          ? undefined
          : selectedKatanaTspIdsSorted,
      path_r_point_ids: receiptSelection.ids.length
        ? receiptSelection.ids
        : undefined,
      path_d_point_ids: deliverySelection.ids.length
        ? deliverySelection.ids
        : undefined,
      view: viewMode,
      limit: nextLimit,
      offset: 0,
    });
  }, [
    startDateInput,
    endDateInput,
    limitInput,
    useAllKatanaTspIds,
    selectedKatanaTspIdsSorted,
    receiptSelection.ids,
    deliverySelection.ids,
    viewMode,
  ]);

  return {
    startDateInput,
    setStartDateInput,
    endDateInput,
    setEndDateInput,
    limitInput,
    setLimitInput,
    hasAppliedFilters,
    offset,
    setOffset,
    clientError,
    katanaTspSearch,
    setKatanaTspSearch,
    katanaTspPageNumber,
    setKatanaTspPageNumber,
    useAllKatanaTspIds,
    setUseAllKatanaTspIds,
    selectedKatanaTspIds,
    selectedKatanaTspIdsSorted,
    receiptSelection,
    deliverySelection,
    activePointModal,
    pointModalSearch,
    pointModalPageNumber,
    setPointModalPageNumber,
    pointModalKatanaTspId,
    rowSearchInput,
    setRowSearchInput,
    rowSearchTerm,
    viewMode,
    setViewMode,
    appliedFilters,
    hiddenColumns,
    debouncedTspSearch,
    debouncedPointSearch,
    toggleTspSelection,
    togglePointSelection,
    clearSelectedPoints,
    openPointModal,
    closePointModal,
    updatePointModalSearch,
    updatePointModalPipeline,
    toggleAdvancedColumns,
    toggleColumn,
    onApplyFilters,
  };
};
